/** A shared playback bus provides gain above 100%, a final limiter, and output
 * device routing. Read MediaStreams directly: MediaElementSource produces
 * silence for srcObject streams in Chromium. SDK elements stay inaudible. */
export class CallMixer {
  private ctx: AudioContext
  private destination: MediaStreamAudioDestinationNode
  private limiter: DynamicsCompressorNode
  private output: HTMLAudioElement
  private tracks = new Map<any, { gain: GainNode; input: HTMLAudioElement; id: string; cleanup: () => void }>()
  constructor(sink: HTMLElement, private gainFor: (id: string) => number) {
    this.ctx = new AudioContext({ latencyHint: 'interactive' })
    this.destination = this.ctx.createMediaStreamDestination()
    this.limiter = this.ctx.createDynamicsCompressor()
    this.limiter.threshold.value = -1; this.limiter.knee.value = 0; this.limiter.ratio.value = 20
    this.limiter.attack.value = 0.001; this.limiter.release.value = 0.08
    this.limiter.connect(this.destination)
    this.output = document.createElement('audio')
    this.output.autoplay = true; this.output.srcObject = this.destination.stream
    sink.appendChild(this.output)
  }
  add(track: any, input: HTMLAudioElement, id: string) {
    this.remove(track)
    const gain = this.ctx.createGain()
    let source: MediaStreamAudioSourceNode | null = null, stream: MediaStream | null = null
    const silenceElement = () => { if (input.volume !== 0) input.volume = 0; if (!input.muted) input.muted = true }
    const refresh = () => {
      if (stream) { stream.removeEventListener('addtrack', refresh); stream.removeEventListener('removetrack', refresh) }
      source?.disconnect(); source = null
      stream = input.srcObject instanceof MediaStream ? input.srcObject : null
      if (stream) {
        stream.addEventListener('addtrack', refresh); stream.addEventListener('removetrack', refresh)
        if (stream.getAudioTracks().length) { source = this.ctx.createMediaStreamSource(stream); source.connect(gain) }
      }
      silenceElement()
    }
    silenceElement(); gain.connect(this.limiter)
    input.addEventListener('volumechange', silenceElement)
    input.addEventListener('loadedmetadata', refresh)
    track.on?.('elementAttached', refresh)
    refresh()
    const cleanup = () => {
      source?.disconnect()
      stream?.removeEventListener('addtrack', refresh); stream?.removeEventListener('removetrack', refresh)
      input.removeEventListener('volumechange', silenceElement); input.removeEventListener('loadedmetadata', refresh)
      track.off?.('elementAttached', refresh)
    }
    this.tracks.set(track, { gain, input, id, cleanup })
    gain.gain.value = this.gainFor(id)
  }
  remove(track: any) {
    const entry = this.tracks.get(track)
    if (!entry) return
    entry.cleanup(); entry.gain.disconnect(); entry.input.remove()
    this.tracks.delete(track)
  }
  update() {
    for (const entry of this.tracks.values()) {
      const value = this.gainFor(entry.id), time = this.ctx.currentTime
      entry.gain.gain.cancelScheduledValues(time)
      if (value === 0) entry.gain.gain.setValueAtTime(0, time)
      else entry.gain.gain.setTargetAtTime(value, time, 0.015)
    }
  }
  async unlock() {
    await this.ctx.resume()
    await this.output.play()
  }
  async setDevice(id: string) {
    const output = this.output as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> }
    await output.setSinkId?.(id || 'default')
  }
  async destroy() {
    for (const track of this.tracks.keys()) this.remove(track)
    this.limiter.disconnect(); this.destination.disconnect()
    this.output.pause(); this.output.srcObject = null; this.output.remove()
    this.destination.stream.getTracks().forEach((t) => t.stop())
    if (this.ctx.state !== 'closed') await this.ctx.close()
  }
}
